import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Check, QuestionMark, X, Minus } from '@phosphor-icons/react';
import clsx from 'clsx';
import { Typography } from '@la/ds-ui-components';
import { getSiteId, useAppSelector } from '@la/services';
import { Activity } from '@la/types';
import { getLAHostnameParts } from '@la/utilities';
import { useGetAttendanceQuery } from 'redux/services/singleActivityApi';
import { Attendance, UserSummary } from 'redux/services/types/singleActivity';
import { getInitials } from 'domains/SingleActivityView/utils/singleActivityView.utils';
import styles from './ActivityAttendance.module.scss';

export default function ActivityAttendance({
  activity,
  userSummaries,
}: {
  activity?: Activity;
  userSummaries: UserSummary[];
}) {
  const { subdomain } = getLAHostnameParts();
  const { programId } = useParams();
  const siteId = useAppSelector(getSiteId) ?? '';

  const activityTeamIds = activity?.teams?.map((team) => team.teamId) || [];
  const idsOfDisplayableTeams =
    userSummaries[0]?.teamIds?.filter((teamId) =>
      activityTeamIds.includes(teamId)
    ) || [];

  const { data: attendance, isLoading: isAttendanceLoading } =
    useGetAttendanceQuery(
      {
        activityId: activity?.id.toString() ?? '',
        programId: programId ?? '',
        siteId: siteId ?? '',
        subdomain,
        teamIds: idsOfDisplayableTeams.join(',') ?? '',
      },
      { skip: !activity || !siteId }
    );

  const {
    yes: yesCount,
    no: noCount,
    maybe: maybeCount,
    no_response: noResponseCount,
  } = useMemo(() => {
    const defaultValue = { yes: 0, no: 0, maybe: 0, no_response: 0 };

    if (!attendance || !attendance.length) {
      return defaultValue;
    }

    return attendance?.reduce((acc: any, curr) => {
      return { ...acc, [curr.response]: (acc[curr.response] || 0) + 1 };
    }, defaultValue);
  }, [attendance]);

  if (isAttendanceLoading) {
    return (
      <Typography variant="ui" size="large">
        Loading...
      </Typography>
    );
  }

  return (
    <>
      <ul className={styles.rsvpStatus}>
        <li className={styles.rsvpStatus_item}>
          <Typography variant="ui" size="medium">
            Going
          </Typography>
          <Typography variant="headline" weight="regular" size="xl">
            {yesCount || 0}
          </Typography>
        </li>
        <li className={styles.rsvpStatus_item}>
          <Typography variant="ui" size="medium">
            Not Going
          </Typography>
          <Typography variant="headline" size="xl">
            {noCount || 0}
          </Typography>
        </li>
        <li className={styles.rsvpStatus_item}>
          <Typography variant="ui" size="medium">
            Maybe
          </Typography>
          <Typography variant="headline" weight="regular" size="xl">
            {maybeCount || 0}
          </Typography>
        </li>
        <li className={styles.rsvpStatus_item}>
          <Typography variant="ui" size="medium">
            No Response
          </Typography>
          <Typography variant="headline" weight="regular" size="xl">
            {noResponseCount || 0}
          </Typography>
        </li>
      </ul>
      <RegistrantAttendanceTable attendance={attendance} />
    </>
  );
}

function RegistrantAttendanceTable({
  attendance,
}: Readonly<{
  attendance?: Attendance[];
}>) {
  return (
    <ul className={styles.userList}>
      {attendance?.map((record: Attendance) => (
        <RegistrantAttendance key={record.userId} record={record} />
      )) || null}
    </ul>
  );
}

function RegistrantAttendance({ record }: Readonly<{ record: Attendance }>) {
  const { firstName, lastName, response } = record;
  return (
    <li className={styles.user}>
      <div className={styles.avatar}>{getInitials(firstName, lastName)}</div>
      <Typography variant="ui" size="large" weight="bold">
        {firstName} {lastName}
      </Typography>
      <div
        className={clsx({
          [styles.user_response]: true,
          [styles.user_response__yes]: response === 'yes',
          [styles.user_response__no]: response === 'no',
          [styles.user_response__maybe]: response === 'maybe',
          [styles.user_response__noRes]: response === 'no_response',
        })}
      >
        {response === 'yes' && <Check size={15} />}
        {response === 'no' && <X size={15} />}
        {response === 'maybe' && <QuestionMark size={15} />}
        {response === 'no_response' && <Minus size={15} />}
      </div>
    </li>
  );
}
